import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AUTH_BLOCKED_MESSAGE_KEY,
  EMAIL_CONFIRMATION_REQUIRED_MESSAGE,
  UserService,
} from '../services/supabase';

interface AuthProps {
  onAuthSuccess: (user: any) => void;
}

export const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(
    searchParams.get('emailVerified') === '1'
      ? 'Email verified. Please sign in to continue.'
      : null
  );
  const navigate = useNavigate();

  React.useEffect(() => {
    const blockedMessage = sessionStorage.getItem(AUTH_BLOCKED_MESSAGE_KEY);
    if (blockedMessage) {
      setError(blockedMessage);
      sessionStorage.removeItem(AUTH_BLOCKED_MESSAGE_KEY);
    }
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setInfo(null);

    try {
      const userService = new UserService();
      const { data, error } = await userService.signIn(email, password);
      if (error) throw error;
      if (data && data.user) {
        onAuthSuccess(data.user);
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      if (err?.message === EMAIL_CONFIRMATION_REQUIRED_MESSAGE) {
        navigate(`/verify-email?reason=unconfirmed&email=${encodeURIComponent(email)}`, {
          replace: true,
        });
        return;
      }
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-xl p-8 shadow-lg">
          {/* Logo and Branding — stacked to fill top of card snugly, same proportions */}
          <div className="flex flex-col items-center gap-4 mb-8 max-w-[360px] mx-auto">
            {/* Text (gradient mask) — scaled up to fill space */}
            <div
              className="w-full h-[68px] bg-gradient-to-r from-primary to-tertiary shrink-0"
              style={{
                WebkitMaskImage: "url('/logos/pink_dolphin_text.png')",
                maskImage: "url('/logos/pink_dolphin_text.png')",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
              role="img"
              aria-label="Pink Dolphin Tracker - Professional Trading Card Analytics"
            />
            {/* Dolphin logo circle — scaled up with text, same proportion */}
            <div className="w-[192px] h-[192px] rounded-full bg-gradient-primary shadow-glow flex items-center justify-center shrink-0">
              <img
                src="/logos/pink_dolphin_logo.png"
                alt=""
                className="h-[147px] w-[147px] object-contain"
                aria-hidden
              />
            </div>
          </div>

          {/* Sign In Form */}
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Enter your password"
                required
              />
            </div>

            {info && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                {info}
              </div>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-dark shadow-md hover:shadow-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};